/* eslint-disable  @typescript-eslint/no-explicit-any */

import { createCache } from 'async-cache-dedupe'
import { EnvDetector } from './EnvDetector'

export const CACHE_METADATA = Symbol('cacheMetadata')

export interface CacheMetadata {
  cached: boolean
  timestamp: number
}
export interface InvalidateCacheConfig<T> {
  name?: string
  references: (args: unknown, result: T) => string[]
}

export interface CacheConfig<I extends new (...args: any[]) => any, O, T = InstanceType<I>> {
  ttl?: number
  references?: (args: unknown, key: string, result: O) => string[] | Promise<string[]>
  asides?: (this: T) => Promise<Record<string, unknown>>
}

const cache = createCache({
  storage: { type: 'memory', options: { invalidation: true } },
})

export function UseCaseCacheInvalidate<O>(
  Target: (new (...args: any[]) => any) & {
    USE_CASE_NAME: string
    prototype: { execute: (...args: any[]) => any }
  },
  config: InvalidateCacheConfig<O>
) {
  if (EnvDetector.create().getEnv() !== 'browser') return Target

  const _execute = Target.prototype.execute

  Object.defineProperty(Target.prototype, 'execute', {
    value: async function execute(...args: unknown[]): Promise<O> {
      const model = (await _execute.apply(this, args)) as O
      if (config.name) {
        const cacheName = config.name
        await Promise.all(
          config.references(args[0], model).map(async ref => cache.invalidate(cacheName, ref))
        )
      }

      await Promise.all(
        config.references(args[0], model).map(async ref => cache.invalidateAll(ref))
      )

      return model
    },
    writable: true,
    enumerable: false,
    configurable: true,
  })
  return Target
}

export function UseCaseCache<I extends new (...args: any[]) => any, O>(
  Target: I & {
    USE_CASE_NAME: string
    prototype: { execute: (...args: any[]) => any }
  },
  config: CacheConfig<I, O> = {}
): I {
  if (EnvDetector.create().getEnv() !== 'browser') return Target

  const name = Target.USE_CASE_NAME
  const _execute = Target.prototype.execute
  const { asides = () => Promise.resolve({}), ...asyncCacheDedupeOptions } = config

  // Track cache hits/misses and deduplication
  let lastRequestWasCacheHit = false
  let lastRequestWasDeduped = false
  let timestamp = Date.now()

  cache.define(
    name,
    {
      onHit: () => {
        lastRequestWasCacheHit = true
        lastRequestWasDeduped = false
        timestamp = Date.now()
      },
      onMiss: () => {
        lastRequestWasCacheHit = false
        lastRequestWasDeduped = false
        timestamp = Date.now()
      },
      onDedupe: () => {
        // Dedupe means the request didn't execute the function, so it's "cached" from caller's perspective
        lastRequestWasDeduped = true
        timestamp = Date.now()
      },
      ...asyncCacheDedupeOptions,
    },
    async function originalExecutor(args: { _self_: unknown; [k: string]: unknown }) {
      return _execute.call(args._self_, args)
    }
  )

  Object.defineProperty(Target.prototype, 'execute', {
    value: async function execute(params?: unknown): Promise<O> {
      const collacterals = await asides.call(this)

      // Call cache without await to capture dedupe state synchronously
      // @ts-expect-error TS limit
      const promise = cache[name]({ ...params, _self_: this, __collaterals__: collacterals })

      // Capture dedupe state immediately (synchronously) after cache call
      // onDedupe is called synchronously, so this captures if request was deduped
      const wasDeduped = lastRequestWasDeduped

      // Now await the promise
      const result = await promise

      // After await, capture hit/miss state (onHit/onMiss are called asynchronously)
      // A request is "cached" if it was either a dedupe or a cache hit
      const wasCached = wasDeduped || lastRequestWasCacheHit
      const ts = timestamp

      // Clone result preserving prototype and all properties to avoid shared mutation
      // when multiple requests are deduped, while keeping class methods intact
      const clonedResult = Object.create(
        Object.getPrototypeOf(result),
        Object.getOwnPropertyDescriptors(result)
      )
      clonedResult[CACHE_METADATA] = {
        cached: wasCached,
        timestamp: ts,
      }

      return clonedResult
    },
    writable: true,
    enumerable: false,
    configurable: true,
  })
  return Target
}
